import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, postDataAPI, putDataAPI, deleteDataAPI } from '../../utils/fetchData';
import { refreshData } from '../../utils/dataPreloader';
import AddToCalendarButton from '../../components/Connectors/AddToCalendarButton';
import { isTourActive } from '../../utils/interactiveTour';
import AppOnboarding from '../../components/AppOnboarding';

const TasksApp = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const [tasks, setTasks] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [sentApprovals, setSentApprovals] = useState([]);

  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'all', label: 'All' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'to-approve', label: 'Approvals' },
    { id: 'sent-for-approval', label: 'Sent' }
  ];

  useEffect(() => {
    fetchTasksData();
  }, []);

  const fetchTasksData = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Fetch all tasks, approvals, and sent approvals in parallel
      const [tasksResponse, approvalsResponse, sentApprovalsResponse] = await Promise.all([
        getDataAPI('user/tasks', token),
        getDataAPI('user/approvals/pending', token),
        getDataAPI('user/approvals/sent', token)
      ]);

      if (tasksResponse?.data?.success) {
        const currentUser = JSON.parse(localStorage.getItem('dizeeUser'));
        const currentUserId = currentUser?._id;
        
        const mappedTasks = (tasksResponse.data.data || []).map(task => {
          // More flexible user ID comparison - check if current user matches any of the task's user IDs
          const taskUserIdStr = task.userId?.toString();
          const createdByIdStr = task.metadata?.createdBy?.toString();
          const currentUserIdStr = currentUserId?.toString();
          const currentUserEmail = currentUser?.email;
          
          // Check if user is creator
          const isCreator = createdByIdStr === currentUserIdStr;
          
          // Check if user is assignee - be more flexible with ID matching
          // Also check if the assignee email matches current user email as fallback
          const isAssignee = taskUserIdStr === currentUserIdStr || 
                           (task.metadata?.assignee_email && task.metadata.assignee_email === currentUserEmail);
          
          const isAssigned = task.metadata?.isAssigned;
          
          return {
            ...task,
            type: 'task',
            id: task._id,
            // Show different info based on user's relationship to the task
            assignedBy: isAssignee && isAssigned ? task.metadata?.assignee_name || 'Team member' : null,
            assignedTo: isCreator && isAssigned ? task.metadata?.assignee_name : null,
            isCreator,
            isAssignee
          };
        });
        setTasks(mappedTasks);
      }
      
      if (approvalsResponse?.data?.success) {
        const mappedApprovals = (approvalsResponse.data.data || []).map(approval => {
          const mapped = {
            ...approval,
            id: approval._id,
            sentBy: approval.requesterId?.username || approval.requesterId?.name || 'Unknown',
            type: 'approval', // Explicitly set type AFTER spread to ensure it overrides
            itemType: approval.type // Store original type as itemType for reference
          };
          return mapped;
        });
        setApprovals(mappedApprovals);
      }
      
      if (sentApprovalsResponse?.data?.success) {
        const mappedSentApprovals = (sentApprovalsResponse.data.data || []).map(approval => {
          const mapped = {
            ...approval,
            id: approval._id,
            sentTo: approval.userId?.username || approval.userId?.name || 'Unknown',
            type: 'sent-approval', // Explicitly set type AFTER spread to ensure it overrides
            itemType: approval.type // Store original type as itemType for reference
          };
          return mapped;
        });
        setSentApprovals(mappedSentApprovals);
      } else {
        console.error('❌ TasksApp - Failed to fetch sent approvals:', sentApprovalsResponse);
      }
    } catch (error) {
      console.error('Error fetching tasks data:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await putDataAPI(`user/tasks/${taskId}/complete`, {}, token);
      
      if (response.data?.success || response?.success) {
        fetchTasksData(); // Refresh all data to get updated statuses
      }
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await deleteDataAPI(`user/tasks/${taskId}`, token);
      
      if (response.data?.success || response?.success) {
        fetchTasksData(); // Refresh all data to remove deleted task
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleApproval = async (approvalId, action) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await putDataAPI(`user/approvals/${approvalId}/${action}`, {}, token);
      
      if (response.data?.success) {
        fetchTasksData(); // Refresh all data to get updated statuses
      }
    } catch (error) {
      console.error('Error handling approval:', error);
    }
  };

  const handleDeleteApproval = async (approvalId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await deleteDataAPI(`user/approvals/${approvalId}`, token);
      
      if (response.data?.success) {
        fetchTasksData(); // Refresh all data to remove deleted approval
      }
    } catch (error) {
      console.error('Error deleting approval:', error);
    }
  };

  const getFilteredItems = () => {
    let filteredItems;
    
    switch (activeTab) {
      case 'all':
        filteredItems = [...tasks, ...approvals, ...sentApprovals];
        break;
      case 'tasks':
        filteredItems = tasks.filter(task => task.type === 'task');
        break;
      case 'to-approve':
        filteredItems = approvals.filter(item => item.type === 'approval');
        break;
      case 'sent-for-approval':
        filteredItems = sentApprovals.filter(item => item.type === 'sent-approval');
        break;
      default:
        filteredItems = [...tasks, ...approvals, ...sentApprovals];
    }
    
    return filteredItems;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-500';
      case 'pending':
        return 'text-yellow-500';
      case 'approved':
        return 'text-green-500';
      case 'denied':
        return 'text-red-500';
      case 'awaiting-reply':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusText = (item) => {
    if (item.type === 'approval') {
      return item.status === 'pending' ? 'Awaiting your approval' : item.status;
    }
    if (item.type === 'sent-approval') {
      return item.status === 'pending' ? 'Awaiting reply' : item.status;
    }
    return item.status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' • ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };


  const getAppTag = (item) => {
    if (!item.metadata?.appType && !item.itemType && !item.type) return null;
    
    // Use metadata.appType first (more specific), then itemType (original type from approval)
    // Don't use item.type as it's now 'approval', 'sent-approval', or 'task'
    const appType = item.metadata?.appType || item.metadata?.itemType || item.itemType;
    const appColors = {
      'booking': { bg: '#FFFF00', text: '#000000', label: 'Booking' },
      'catalog': { bg: '#17A2B8', text: '#FFFFFF', label: 'Catalog' },
      'tour': { bg: '#FF9500', text: '#FFFFFF', label: 'Tour' },
      'flight': { bg: '#FF9500', text: '#FFFFFF', label: 'Flight' },
      'hotel': { bg: '#FF9500', text: '#FFFFFF', label: 'Hotel' },
      'transport': { bg: '#FF9500', text: '#FFFFFF', label: 'Transport' },
      'task': { bg: '#00CED1', text: '#FFFFFF', label: 'Task' },
      'reminder': { bg: '#00CED1', text: '#FFFFFF', label: 'Reminder' },
      'workflow': { bg: '#AAFF00', text: '#000000', label: 'Workflow' }
    };

    const config = appColors[appType] || { bg: '#8E8E93', text: '#FFFFFF', label: 'General' };
    
    return (
      <span 
        className="px-2 py-1 rounded text-[9px] font-bold whitespace-nowrap"
        style={{ 
          backgroundColor: config.bg, 
          color: config.text 
        }}
      >
        {config.label}
      </span>
    );
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Tasks</span>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate('/tasks/quick-add')}
              >
                New Task
              </span>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  // In tour mode, always go back to home to preserve tour state
                  if (isTourActive()) {
                    navigate('/');
                  } else {
                    navigate(-1);
                  }
                }}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#00CED1]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto -mt-6">
          {activeTab === 'home' ? (
            <div className="flex flex-col gap-4 mb-8">
              {/* Onboarding - shows when no tasks */}
              {tasks.length === 0 && (
                <div className="pt-4">
                  <AppOnboarding
                    appName="Tasks"
                    description="Create and manage tasks for yourself and your team to keep everything on track."
                    color="#00CED1"
                    steps={[
                      {
                        id: 'create-task',
                        title: 'Create your first task',
                        description: 'Add a task with due date and priority',
                        completed: false,
                        onClick: () => navigate('/tasks/quick-add')
                      },
                      {
                        id: 'assign-team',
                        title: 'Assign to team members',
                        description: 'Delegate tasks to your team',
                        completed: false,
                        onClick: () => navigate('/tasks/quick-add')
                      }
                    ]}
                    actionText="Create First Task"
                    onAction={() => navigate('/tasks/quick-add')}
                    icon={
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                  />
                </div>
              )}
              
              <div className="mb-4 pt-8">
                <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5] mb-6">
                  Stay organized and keep your entire team moving with clear, actionable to do list style tasks.
                </p>
              </div>

              <div
                onClick={() => navigate('/tasks/quick-add')}
                className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#00CED1] hover:bg-[rgba(0,206,209,0.05)] transition-all cursor-pointer"
              >
                <h3 className="text-white text-[12px] font-normal mb-2">Create Task</h3>
                <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                  Add a new task with due dates, notes, and priority to keep every project on track.
                </p>
              </div>

              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <h3 className="text-white text-[12px] font-normal mb-2">Assign to Team Members</h3>
                <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                  Send tasks directly to anyone on your team so responsibilities are always clear.
                </p>
              </div>

              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <h3 className="text-white text-[12px] font-normal mb-2">Set Follow-Up Reminders</h3>
                <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                  Create personal or team reminders to make sure nothing slips through the cracks.
                </p>
              </div>

              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <h3 className="text-white text-[12px] font-normal mb-2">Tasks Status:</h3>
                <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                  See what needs attention now with a focused view of tasks due soon and overdue and review all completed tasks.
                </p>
              </div>
            </div>
          ) : (() => {
            const filteredItems = getFilteredItems();
            
            if (filteredItems.length === 0) {
              return (
            <div className="text-left py-[60px]">
              <h3 className="text-white text-[12px] font-normal mb-2">
                {activeTab === 'tasks' && 'No tasks found'}
                {activeTab === 'to-approve' && 'No approvals found'}
                {activeTab === 'sent-for-approval' && 'No sent approvals found'}
                {activeTab === 'all' && 'No items found'}
              </h3>
              <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                {activeTab === 'tasks' && 'Create your first task to get started.'}
                {activeTab === 'to-approve' && 'No items are waiting for your approval.'}
                {activeTab === 'sent-for-approval' && 'No approvals have been sent yet.'}
                {activeTab === 'all' && 'Create your first task or approval to get started.'}
              </p>
              {activeTab === 'tasks' && (
                <button
                  onClick={() => navigate('/tasks/quick-add')}
                  className="bg-[#00CED1] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(0,206,209,0.9)] transition-colors"
                >
                  New Task
                </button>
              )}
            </div>
              );
            }
            
            return (
            <div className="space-y-2 py-4">
              {filteredItems.map((item) => {
                
                return (
                <div 
                  key={item.id} 
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF2300] hover:bg-[rgba(255,35,0,0.05)] transition-all"
                >
                  {/* Header with title and action buttons */}
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-white text-[12px] font-normal">
                      {(() => {
                        // Catalog items - check metadata.appType as well
                        if (item.itemType === 'catalog' || item.metadata?.appType === 'catalog') {
                          const songTitle = item.metadata?.catalogData?.song_title || 
                                          item.metadata?.title ||
                                          item.metadata?.catalogData?.song_name ||
                                          item.metadata?.catalogData?.name ||
                                          item.song_title || 
                                          item.song_name || 
                                          item.name ||
                                          'Unknown Song';
                          return `Catalog: ${songTitle}`;
                        }
                        
                        // Flight items - check metadata.appType since type might be 'tour'
                        if (item.itemType === 'flight' || item.metadata?.appType === 'flight') {
                          const departure = item.metadata?.departure?.airport || 'XXX';
                          const arrival = item.metadata?.arrival?.airport || 'XXX';
                          return `Flight: ${departure} to ${arrival}`;
                        }
                        
                        // Hotel items - check metadata.appType since type might be 'tour'
                        if (item.itemType === 'hotel' || item.metadata?.appType === 'hotel') {
                          return `Hotel: ${item.metadata?.hotelName || item.hotelName || item.name || 'Unknown Hotel'}`;
                        }
                        
                        // Default to title
                        return item.title;
                      })()}
                    </h3>
                    <div className="flex items-center gap-2">
                      {/* Action buttons - top right */}
                      {item.type === 'task' && item.status === 'pending' && (
                        <>
                          {/* Only assignee can complete the task */}
                          {item.isAssignee && (
                            <button
                              onClick={() => handleCompleteTask(item.id)}
                              className="text-[#34C759] text-[10px] px-2 py-1 hover:bg-[rgba(52,199,89,0.1)] rounded transition-colors"
                            >
                              Complete
                            </button>
                          )}
                          {/* Both creator and assignee can delete */}
                          <button
                            onClick={() => handleDeleteTask(item.id)}
                            className="text-[#00CED1] text-[10px] px-2 py-1 hover:bg-[rgba(0,206,209,0.1)] rounded transition-colors"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      
                      {item.type === 'task' && item.status === 'completed' && (
                        <button
                          onClick={() => handleDeleteTask(item.id)}
                          className="text-[#8E8E93] text-[10px] px-2 py-1 hover:bg-[rgba(142,142,147,0.1)] rounded transition-colors"
                        >
                          Clear
                        </button>
                      )}
                      
                      {item.type === 'approval' && item.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApproval(item.id, 'approve')}
                            className="text-[#34C759] text-[10px] px-2 py-1 hover:bg-[rgba(52,199,89,0.1)] rounded transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleApproval(item.id, 'deny')}
                            className="text-[#00CED1] text-[10px] px-2 py-1 hover:bg-[rgba(0,206,209,0.1)] rounded transition-colors"
                          >
                            Deny
                          </button>
                        </>
                      )}
                      
                      {/* Delete/Clear button for sent approvals */}
                      {item.type === 'sent-approval' && (
                        <button
                          onClick={() => handleDeleteApproval(item.id)}
                          className={`${
                            item.status === 'pending' 
                              ? 'text-[#00CED1] hover:bg-[rgba(0,206,209,0.1)]' 
                              : 'text-[#8E8E93] hover:bg-[rgba(142,142,147,0.1)]'
                          } text-[10px] px-2 py-1 rounded transition-colors`}
                        >
                          {item.status === 'pending' ? 'Cancel' : 'Clear'}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {item.description && (
                    <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-2">{item.description}</p>
                  )}
                  
                  <div className="flex items-center gap-2 text-[11px] text-[rgba(255,255,255,0.5)] mb-2">
                    {item.dueDate && (
                      <span>Due {formatDate(item.dueDate)}</span>
                    )}
                    {item.assignedBy && (
                      <span>From {item.assignedBy}</span>
                    )}
                    {item.assignedTo && (
                      <span>To {item.assignedTo}</span>
                    )}
                    {item.sentBy && (
                      <span>From {item.sentBy}</span>
                    )}
                    {item.sentTo && (
                      <span>To {item.sentTo}</span>
                    )}
                  </div>
                  
                  {/* Bottom row: App tag, calendar button, and status */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      {getAppTag(item)}
                      
                      {/* Add to Calendar button for tasks with due dates */}
                      {item.dueDate && (
                        <AddToCalendarButton
                          eventData={{
                            summary: (() => {
                              if (item.itemType === 'catalog' || item.metadata?.appType === 'catalog') {
                                return `Catalog: ${item.metadata?.catalogData?.song_title || item.title}`;
                              }
                              if (item.itemType === 'flight' || item.metadata?.appType === 'flight') {
                                const departure = item.metadata?.departure?.airport || 'XXX';
                                const arrival = item.metadata?.arrival?.airport || 'XXX';
                                return `Flight: ${departure} to ${arrival}`;
                              }
                              if (item.itemType === 'hotel' || item.metadata?.appType === 'hotel') {
                                return `Hotel: ${item.metadata?.hotelName || item.hotelName || 'Hotel'}`;
                              }
                              return item.title;
                            })(),
                            date: item.dueDate,
                            description: item.description || `Task: ${item.title}`
                          }}
                          iconOnly={true}
                          compact={true}
                        />
                      )}
                    </div>
                    <span 
                      className="px-2 py-1 rounded text-[9px] font-bold tracking-wider"
                      style={{
                        color: item.status === 'completed' ? '#FFFFFF' :
                               item.status === 'pending' ? '#000000' :
                               item.status === 'approved' ? '#FFFFFF' :
                               item.status === 'denied' ? '#FFFFFF' :
                               item.status === 'awaiting-reply' ? '#000000' : '#FFFFFF',
                        backgroundColor: item.status === 'completed' ? '#34C759' :
                                        item.status === 'pending' ? '#FFFF00' :
                                        item.status === 'approved' ? '#34C759' :
                                        item.status === 'denied' ? '#00CED1' :
                                        item.status === 'awaiting-reply' ? '#FFFF00' : '#8E8E93'
                      }}
                    >
                      {getStatusText(item).toUpperCase()}
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
            );
          })()}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};


export default TasksApp;
